// ============================================================
// Page Prix multi-niveau + Promotions — Lot L14 (frontend)
// ============================================================
// 3 onglets :
//   1. Niveaux de prix (gros / détail / VIP / etc.) — propriétaire
//   2. Prix par variante × niveau × boutique — gérant possible
//   3. Promotions (% remise temporaire) — gérant
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import InputMontant from '../../components/common/InputMontant';
import { getDeviseCourante } from '../../utils/format';
import {
  Tag, Plus, X, Loader, Trash2, Edit2, ToggleRight, ToggleLeft,
  Percent, Ban,
} from 'lucide-react';
import toast from 'react-hot-toast';

function fmt(n) { return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante(); }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString('fr-FR') : '—'; }

// ─── Onglet Niveaux de prix ──────────────────────────────────
function OngletNiveaux() {
  const { canEditParametres } = useAuth();
  const [niveaux, setNiveaux] = useState([]);
  const [modal, setModal] = useState(null);

  const charger = () => api.get('/niveaux-prix').then(r => setNiveaux(r.data || []));
  useEffect(() => { charger(); }, []);

  const enregistrer = async (form) => {
    try {
      if (form.id) await api.put(`/niveaux-prix/${form.id}`, form);
      else          await api.post('/niveaux-prix', form);
      toast.success('Niveau enregistré');
      setModal(null); charger();
    } catch (err) { toast.error(err.response?.data?.message || 'Échec'); }
  };
  const toggle = async (n) => {
    try {
      await api.patch(`/niveaux-prix/${n.id}/statut`, { actif: !n.actif });
      charger();
    } catch { toast.error('Échec'); }
  };

  return (
    <div className="space-y-3">
      {canEditParametres && (
        <div className="flex justify-end">
          <button onClick={() => setModal({ code: '', nom: '', actif: true })}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">
            <Plus size={14} /> Nouveau niveau
          </button>
        </div>
      )}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/50">
            <tr className="text-left text-xs text-gray-400">
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Nom</th>
              <th className="px-3 py-2">État</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {niveaux.length === 0 ? (
              <tr><td colSpan={4} className="text-center text-gray-500 py-8">Aucun niveau de prix</td></tr>
            ) : niveaux.map(n => (
              <tr key={n.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                <td className="px-3 py-2 font-mono text-xs text-gray-300">{n.code}</td>
                <td className="px-3 py-2 text-gray-300">{n.nom}</td>
                <td className="px-3 py-2">
                  {canEditParametres ? (
                    <button onClick={() => toggle(n)} className="text-gray-400 hover:text-white">
                      {n.actif ? <ToggleRight size={18} className="text-emerald-400" /> : <ToggleLeft size={18} />}
                    </button>
                  ) : (n.actif ? <span className="text-emerald-400 text-xs">Actif</span> : <span className="text-gray-500 text-xs">Désactivé</span>)}
                </td>
                <td className="px-3 py-2 text-right">
                  {canEditParametres && (
                    <button onClick={() => setModal(n)} className="text-gray-400 hover:text-white"><Edit2 size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{modal.id ? 'Modifier' : 'Nouveau'} niveau</h3>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input type="text" value={modal.code || ''}
                onChange={e => setModal({ ...modal, code: e.target.value })}
                placeholder="Code (ex: gros, vip)"
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
              <input type="text" value={modal.nom || ''}
                onChange={e => setModal({ ...modal, nom: e.target.value })}
                placeholder="Nom affiché"
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">Annuler</button>
              <button onClick={() => enregistrer(modal)}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Prix par variante ────────────────────────────────
function OngletPrixVariantes() {
  const [prix, setPrix] = useState([]);
  const [variantes, setVariantes] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    variante_id: '', niveau_prix_id: '', boutique_id: '', prix: 0,
  });

  const charger = () => api.get('/prix-variantes').then(r => setPrix(r.data || []));
  useEffect(() => {
    charger();
    api.get('/produits/variantes').then(r => setVariantes(r.data || []));
    api.get('/niveaux-prix').then(r => setNiveaux(r.data || []));
    api.get('/boutiques').then(r => setBoutiques(r.data || []));
  }, []);

  const submit = async () => {
    try {
      await api.put('/prix-variantes', {
        variante_id: form.variante_id,
        niveau_prix_id: form.niveau_prix_id,
        boutique_id: form.boutique_id || null,
        prix: parseInt(form.prix) || 0,
      });
      toast.success('Prix enregistré');
      setShowModal(false); charger();
    } catch (err) { toast.error(err.response?.data?.message || 'Échec'); }
  };

  const supprimer = async (id) => {
    if (!window.confirm('Supprimer ce prix ?')) return;
    try { await api.delete(`/prix-variantes/${id}`); charger(); } catch { toast.error('Échec'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">
          <Plus size={14} /> Nouveau prix
        </button>
      </div>
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/50">
            <tr className="text-left text-xs text-gray-400">
              <th className="px-3 py-2">Variante</th>
              <th className="px-3 py-2">Niveau</th>
              <th className="px-3 py-2">Boutique</th>
              <th className="px-3 py-2 text-right">Prix</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {prix.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-gray-500 py-8">Aucun prix défini — un seul prix universel par variante</td></tr>
            ) : prix.map(p => (
              <tr key={p.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                <td className="px-3 py-2 text-gray-300 text-xs">{p.produit_nom} {p.modele || p.code_variante}</td>
                <td className="px-3 py-2 text-gray-400 text-xs">{p.niveau_nom}</td>
                <td className="px-3 py-2 text-gray-400 text-xs">{p.boutique_nom || <span className="text-gray-500 italic">Toutes</span>}</td>
                <td className="px-3 py-2 text-right text-white tabular-nums">{fmt(p.prix)}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => supprimer(p.id)} className="text-gray-500 hover:text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Nouveau prix variante</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <select value={form.variante_id} onChange={e => setForm({ ...form, variante_id: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
                <option value="">— Variante —</option>
                {variantes.map(v => <option key={v.id} value={v.id}>{v.produit_nom} · {v.modele || v.code_variante}</option>)}
              </select>
              <select value={form.niveau_prix_id} onChange={e => setForm({ ...form, niveau_prix_id: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
                <option value="">— Niveau —</option>
                {niveaux.filter(n => n.actif).map(n => <option key={n.id} value={n.id}>{n.nom}</option>)}
              </select>
              <select value={form.boutique_id} onChange={e => setForm({ ...form, boutique_id: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
                <option value="">Toutes les boutiques</option>
                {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
              </select>
              <InputMontant value={form.prix} onChange={v => setForm({ ...form, prix: v })}
                placeholder={`Prix en ${getDeviseCourante()}`}
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white text-right tabular-nums" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">Annuler</button>
              <button onClick={submit}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Promotions ───────────────────────────────────────
function OngletPromotions() {
  const { canEditBusiness, canEditParametres } = useAuth();
  const [promos, setPromos] = useState([]);
  const [modal, setModal] = useState(null);

  const charger = () => api.get('/promotions').then(r => setPromos(r.data || []));
  useEffect(() => { charger(); }, []);

  const enregistrer = async (f) => {
    try {
      const payload = {
        ...f,
        pourcentage_remise: parseFloat(f.pourcentage_remise) || 0,
      };
      if (f.id) await api.put(`/promotions/${f.id}`, payload);
      else      await api.post('/promotions', payload);
      toast.success('Promotion enregistrée');
      setModal(null); charger();
    } catch (err) { toast.error(err.response?.data?.message || 'Échec'); }
  };
  const annuler = async (p) => {
    if (!window.confirm('Annuler cette promotion ?')) return;
    try { await api.patch(`/promotions/${p.id}/annuler`); charger(); } catch { toast.error('Échec'); }
  };

  return (
    <div className="space-y-3">
      {canEditBusiness && (
        <div className="flex justify-end">
          <button onClick={() => setModal({ libelle: '', pourcentage_remise: 0, date_debut: '', date_fin: '' })}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">
            <Plus size={14} /> Nouvelle promotion
          </button>
        </div>
      )}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/50">
            <tr className="text-left text-xs text-gray-400">
              <th className="px-3 py-2">Libellé</th>
              <th className="px-3 py-2 text-right">Remise</th>
              <th className="px-3 py-2">Période</th>
              <th className="px-3 py-2">Statut</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {promos.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-gray-500 py-8">Aucune promotion</td></tr>
            ) : promos.map(p => (
              <tr key={p.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                <td className="px-3 py-2 text-gray-300">{p.libelle}</td>
                <td className="px-3 py-2 text-right text-yellow-400 tabular-nums">{p.pourcentage_remise}%</td>
                <td className="px-3 py-2 text-gray-400 text-xs">{fmtDate(p.date_debut)} → {fmtDate(p.date_fin)}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs border ${
                    p.statut === 'active' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
                    p.statut === 'annulee' ? 'bg-gray-500/15 text-gray-400 border-gray-600/30' :
                    'bg-amber-500/15 text-amber-300 border-amber-500/30'
                  }`}>{p.statut}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  {canEditBusiness && p.statut === 'active' && (
                    <button onClick={() => annuler(p)} className="text-gray-500 hover:text-red-400" title="Annuler">
                      <Ban size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{modal.id ? 'Modifier' : 'Nouvelle'} promotion</h3>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input type="text" value={modal.libelle || ''}
                onChange={e => setModal({ ...modal, libelle: e.target.value })}
                placeholder="Libellé (ex: Soldes d'été)"
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
              <div className="flex items-center gap-2">
                <Percent size={14} className="text-gray-400" />
                <input type="number" min="0" max="100" step="0.5" value={modal.pourcentage_remise || 0}
                  onChange={e => setModal({ ...modal, pourcentage_remise: e.target.value })}
                  placeholder="Remise %"
                  className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white tabular-nums" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-400">Du</label>
                  <input type="date" value={modal.date_debut || ''}
                    onChange={e => setModal({ ...modal, date_debut: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
                </div>
                <div>
                  <label className="text-xs text-gray-400">Au</label>
                  <input type="date" value={modal.date_fin || ''}
                    onChange={e => setModal({ ...modal, date_fin: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">Annuler</button>
              <button onClick={() => enregistrer(modal)}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PagePrixPromotions() {
  const [tab, setTab] = useState('niveaux');
  return (
    <div className="p-4 sm:p-6 space-y-4">
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 bg-yellow-500/15 border border-yellow-500/30 rounded-lg flex items-center justify-center">
          <Tag size={18} className="text-yellow-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">Prix & Promotions</h1>
          <p className="text-xs text-gray-500">Niveaux de prix, prix par boutique, promotions temporaires</p>
        </div>
      </header>

      <div className="border-b border-gray-800 flex gap-1">
        {[
          { k: 'niveaux',    label: 'Niveaux de prix' },
          { k: 'variantes',  label: 'Prix variantes' },
          { k: 'promotions', label: 'Promotions' },
        ].map(o => (
          <button key={o.k} onClick={() => setTab(o.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === o.k ? 'border-yellow-500 text-yellow-400' : 'border-transparent text-gray-400 hover:text-white'
            }`}>{o.label}</button>
        ))}
      </div>

      {tab === 'niveaux'    && <OngletNiveaux />}
      {tab === 'variantes'  && <OngletPrixVariantes />}
      {tab === 'promotions' && <OngletPromotions />}
    </div>
  );
}
